import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { ExternalLink, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import './UnexploredLinks.css';

// =========================================
// Main Component
// =========================================
const UnexploredLinks = () => {
  // =========================================
  // State Management
  // =========================================
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // =========================================
  // Data Loading
  // =========================================
  const loadLinks = useCallback(async (page) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.unexploredLinks.getLinks(page);
      setLinks(response.links || []);
      setTotalPages(Number(response.totalPages) || 1);
    } catch (err) {
      console.error('Error loading unexplored links:', err);
      setError('Failed to load unexplored links');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // =========================================
  // Effects
  // =========================================
  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      setIsLoading(false);
      return;
    }
    loadLinks(currentPage);
  }, [currentPage, isAuthenticated, authLoading, loadLinks]);

  // =========================================
  // Event Handlers
  // =========================================
  const handleDeleteLink = async (id) => {
    try {
      await apiService.unexploredLinks.deleteLink(id);
      await loadLinks(currentPage);
    } catch (err) {
      console.error('Error deleting link:', err);
      if (err.response?.status !== 500) {
        setError('Failed to delete link');
      } else {
        await loadLinks(currentPage);
      }
    }
  };

  // =========================================
  // Component Render
  // =========================================
  return (
    <div className="ul-page">
      <div className="ul-results-container">
        <div className="ul-table-wrapper">
          <table className="ul-results-list">
            <thead>
              <tr>
                <th className="ul-col-added">Added</th>
                <th className="ul-col-title">Title</th>
                <th className="ul-col-from">From</th>
                <th className="ul-col-actions">Delete</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="ul-loading-message">
                    Loading unexplored links...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="4" className="ul-error-message">
                    {error}
                  </td>
                </tr>
              ) : links.length === 0 ? (
                <tr>
                  <td colSpan="4" className="ul-no-links">
                    No unexplored links found.
                  </td>
                </tr>
              ) : (
                links.map((link) => (
                  <tr key={link._id}>
                    <td className="ul-col-added">
                      {link.createdAt
                        ? format(new Date(link.createdAt), 'MM/dd/yyyy')
                        : 'No date'}
                    </td>
                    <td className="ul-col-title ul-cell-with-icons">
                      <div className="ul-cell-content">
                        {link.docTitle || link.docUrl}
                      </div>
                      <div className="ul-icon-container">
                        {link.docUrl && (
                          <a
                            href={link.docUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ul-icon-button"
                            title="Open link"
                          >
                            <ExternalLink size={20} />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="ul-col-from">
                      <div className="ul-cell-content" title={link.sourceText}>
                        {link.sourceText}
                      </div>
                    </td>
                    <td className="ul-col-actions">
                      <div className="ul-icon-container">
                        <button
                          className="ul-icon-button ul-delete-button"
                          onClick={() => handleDeleteLink(link._id)}
                          title="Delete link"
                          aria-label="Delete link"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="ul-pagination">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1 || isLoading}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || isLoading}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnexploredLinks;
